import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

// Synced Purchase pages — render inside tabs (đồng bộ pattern /sales/contract/<tab>)
import LocalPListPage from './purchase/LocalPListPage'
import ContractsAllListPage from './purchase/ContractsAllListPage'
import NPEContractListPage from './purchase/NPEContractListPage'
import ForwardListPage from './purchase/ForwardListPage'
import AnnexListPage from './purchase/AnnexListPage'
import PTBFFixationListPage from './purchase/PTBFFixationListPage'
import PTBFRollingListPage from './purchase/PTBFRollingListPage'
import NPEFixationListPage from './purchase/NPEFixationListPage'
import NPEAllocationListPage from './purchase/NPEAllocationListPage'
import CSFixationListPage from './purchase/CSFixationListPage'
import LocalPPlansPage from './purchase/LocalPPlansPage'
import PageHeader from '../components/PageHeader'

type PurchaseTabKey =
  | 'localP'
  | 'contractsAll'
  | 'npeContract'
  | 'forwardP'
  | 'amendment'
  | 'ptbfFixation'
  | 'ptbfRolling'
  | 'npeFixation'
  | 'npeAllocation'
  | 'csFixation'
  | 'plans'

const TAB_CONFIG: { key: PurchaseTabKey; label: string; segment: string }[] = [
  { key: 'localP', label: 'PU Contract', segment: 'local-p' },
  { key: 'contractsAll', label: 'All Contracts', segment: 'all-contracts' },
  { key: 'npeContract', label: 'NPE Contract', segment: 'npe-contract' },
  { key: 'forwardP', label: 'Forward PU', segment: 'forward-pu' },
  { key: 'amendment', label: 'Amendment', segment: 'amendment' },
  { key: 'ptbfFixation', label: 'PTBF Fixation', segment: 'ptbf-fixation' },
  { key: 'ptbfRolling', label: 'PTBF Rolling', segment: 'ptbf-rolling' },
  { key: 'npeFixation', label: 'NPE Fixation', segment: 'npe-fixation' },
  { key: 'npeAllocation', label: 'NPE Allocation', segment: 'npe-allocation' },
  { key: 'csFixation', label: 'CS Fixation', segment: 'cs-fixation' },
  { key: 'plans', label: 'Plans', segment: 'plans' },
]

function isPurchaseTabKey(value: string | null): value is PurchaseTabKey {
  return TAB_CONFIG.some((tab) => tab.key === value)
}

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

const headerLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

const compactInputSx = {
  width: { xs: 124, sm: 132 },
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiInputBase-input': {
    py: 0.25,
    px: 0.75,
  },
}

const compactSelectSx = {
  width: 104,
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiSelect-select': {
    py: 0.25,
    px: 0.75,
  },
}

export default function PurchaseContractPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(location.search)
  const queryTab = searchParams.get('tab')
  const routeSegment = location.pathname.split('/').filter(Boolean)[2]
  const routeTab = TAB_CONFIG.find((tab) => tab.segment === routeSegment)?.key

  const [activeTab, setActiveTab] = useState<PurchaseTabKey>(
    routeTab ?? (isPurchaseTabKey(queryTab) ? queryTab : 'localP'),
  )
  const [deliveryLatestFrom, setDeliveryLatestFrom] = useState('')
  const [deliveryLatestTo, setDeliveryLatestTo] = useState('')
  const [isForcedClose, setIsForcedClose] = useState<'all' | 'yes' | 'no'>('all')
  const [qualityPriceOpen, setQualityPriceOpen] = useState(false)

  useEffect(() => {
    const nextTab = routeTab ?? (isPurchaseTabKey(queryTab) ? queryTab : null)
    if (nextTab && nextTab !== activeTab) {
      setActiveTab(nextTab)
    }
  }, [routeTab, queryTab, activeTab])

  useEffect(() => {
    if (!location.pathname.startsWith('/purchase/contract')) return

    if (!routeTab) {
      navigate('/purchase/contract/local-p', { replace: true })
    }
  }, [location.pathname, navigate, routeTab])

  const current = TAB_CONFIG.find((t) => t.key === activeTab) ?? TAB_CONFIG[0]
  const hasInvalidRange = deliveryLatestFrom !== '' && deliveryLatestTo !== '' && deliveryLatestFrom > deliveryLatestTo

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Purchase Contract"
        module="Purchase"
        actions={
        <Stack
          direction="row"
          spacing={0.45}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
              Delivery Latest:
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                value={deliveryLatestFrom ? dayjs(deliveryLatestFrom) : null}
                onChange={(d) => setDeliveryLatestFrom(d ? d.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', sx: compactInputSx, inputProps: { 'aria-label': 'Delivery Latest start date' } } }}
              />
              <DatePicker
                format="DD/MM/YYYY"
                value={deliveryLatestTo ? dayjs(deliveryLatestTo) : null}
                onChange={(d) => setDeliveryLatestTo(d ? d.format('YYYY-MM-DD') : '')}
                slotProps={{ textField: { size: 'small', error: hasInvalidRange, sx: compactInputSx, inputProps: { 'aria-label': 'Delivery Latest end date' } } }}
              />
            </LocalizationProvider>
          </Stack>

          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
              Is Forced Close:
            </Typography>
            <TextField
              select
              size="small"
              value={isForcedClose}
              onChange={(event) => setIsForcedClose(event.target.value as 'all' | 'yes' | 'no')}
              sx={compactSelectSx}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Stack>

          <Button size="small" variant="outlined" sx={headerButtonSx} onClick={() => setQualityPriceOpen(true)}>
            Quality Price (vs Basis)
          </Button>
          <Button size="small" variant="outlined" sx={headerButtonSx} disabled={hasInvalidRange}>
            Export (Filter)
          </Button>
          <Button size="small" variant="outlined" sx={headerButtonSx} disabled={hasInvalidRange}>
            Export (All data)
          </Button>
        </Stack>
        }
      />
      {hasInvalidRange ? (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      ) : null}
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeTab}
          className="pc-purchase-tabs"
          onChange={(_, value) => {
            const tab = TAB_CONFIG.find((t) => t.key === value)
            if (tab) navigate(`/purchase/contract/${tab.segment}`, { replace: false })
            setActiveTab(value)
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>
        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {activeTab === 'localP' && <LocalPListPage />}
          {activeTab === 'contractsAll' && <ContractsAllListPage />}
          {activeTab === 'npeContract' && <NPEContractListPage />}
          {activeTab === 'forwardP' && <ForwardListPage />}
          {activeTab === 'amendment' && <AnnexListPage />}
          {activeTab === 'ptbfFixation' && <PTBFFixationListPage />}
          {activeTab === 'ptbfRolling' && <PTBFRollingListPage />}
          {activeTab === 'npeFixation' && <NPEFixationListPage />}
          {activeTab === 'npeAllocation' && <NPEAllocationListPage />}
          {activeTab === 'csFixation' && <CSFixationListPage />}
          {activeTab === 'plans' && <LocalPPlansPage />}
        </CardContent>
      </Card>

      <Dialog open={qualityPriceOpen} onClose={() => setQualityPriceOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Quality Price (vs Basis)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Tính năng Quality Price cho Purchase Contract đang được bổ sung.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQualityPriceOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

